import {
  NL_SEARCH_IDLE_MAX_ITEMS,
  NL_SEARCH_RECENT_MAX,
  NL_SEARCH_RECENT_SCOPE_MAP,
  NL_SEARCH_RECENT_SCOPE_SHARED,
  NL_SEARCH_SUGGESTED_QUERIES,
} from '../data/nlSearchConstants';

const STORAGE_KEY_PREFIX = 'terrio.nlSearch.recent.v1';

/**
 * Persistencia v1: localStorage por usuario Cognito (`sub`) y por vista.
 * Mapa: `terrio.nlSearch.recent.v1.<sub>` (key histórica, no migrar).
 * Share: `terrio.nlSearch.recent.v1.shared-list.<sub>`.
 * Al logout, `signOut` borra ambos via `clearAllNlSearchRecentQueries`.
 */

function resolveScope(scope) {
  return scope === NL_SEARCH_RECENT_SCOPE_SHARED
    ? NL_SEARCH_RECENT_SCOPE_SHARED
    : NL_SEARCH_RECENT_SCOPE_MAP;
}

/**
 * @param {string|null|undefined} userKey
 * @param {string} [scope]
 * @returns {string}
 */
export function nlSearchRecentStorageKey(userKey, scope = NL_SEARCH_RECENT_SCOPE_MAP) {
  const id = userKey || 'anon';
  if (resolveScope(scope) === NL_SEARCH_RECENT_SCOPE_SHARED) {
    return `${STORAGE_KEY_PREFIX}.${NL_SEARCH_RECENT_SCOPE_SHARED}.${id}`;
  }
  return `${STORAGE_KEY_PREFIX}.${id}`;
}

function normalizeQuery(query) {
  return String(query || '').trim().replace(/\s+/g, ' ');
}

function normalizeKey(query) {
  return normalizeQuery(query).toLowerCase();
}

/**
 * @param {string|null|undefined} userKey Cognito `sub` (o email).
 * @param {string} [scope]
 * @returns {{ query: string, at: number }[]}
 */
export function readNlSearchRecentQueries(userKey, scope = NL_SEARCH_RECENT_SCOPE_MAP) {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(nlSearchRecentStorageKey(userKey, scope));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => ({
        query: normalizeQuery(item?.query),
        at: Number(item?.at) || 0,
      }))
      .filter((item) => item.query.length > 0)
      .sort((a, b) => b.at - a.at)
      .slice(0, NL_SEARCH_RECENT_MAX);
  } catch {
    return [];
  }
}

/**
 * Dedup case-insensitive: si ya existe, sube al tope con `at` nuevo.
 * @param {string|null|undefined} userKey
 * @param {string} query
 * @param {string} [scope]
 * @returns {{ query: string, at: number }[]}
 */
export function rememberNlSearchQuery(userKey, query, scope = NL_SEARCH_RECENT_SCOPE_MAP) {
  const text = normalizeQuery(query);
  if (!text || typeof window === 'undefined' || !window.localStorage) {
    return readNlSearchRecentQueries(userKey, scope);
  }

  const key = normalizeKey(text);
  const now = Date.now();
  const next = [
    { query: text, at: now },
    ...readNlSearchRecentQueries(userKey, scope).filter((item) => normalizeKey(item.query) !== key),
  ].slice(0, NL_SEARCH_RECENT_MAX);

  try {
    window.localStorage.setItem(nlSearchRecentStorageKey(userKey, scope), JSON.stringify(next));
  } catch {
    // Quota / modo privado: el UI sigue con el array en memoria del caller.
  }
  return next;
}

/**
 * Quita una frase de Recientes (case-insensitive). Si queda vacío, borra la key.
 * @param {string|null|undefined} userKey
 * @param {string} query
 * @param {string} [scope]
 * @returns {{ query: string, at: number }[]}
 */
export function forgetNlSearchQuery(userKey, query, scope = NL_SEARCH_RECENT_SCOPE_MAP) {
  const key = normalizeKey(query);
  if (!key || typeof window === 'undefined' || !window.localStorage) {
    return readNlSearchRecentQueries(userKey, scope);
  }

  const next = readNlSearchRecentQueries(userKey, scope)
    .filter((item) => normalizeKey(item.query) !== key);

  try {
    const storageKey = nlSearchRecentStorageKey(userKey, scope);
    if (next.length === 0) {
      window.localStorage.removeItem(storageKey);
    } else {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    }
  } catch {
    // Quota / modo privado: el caller sigue con el array en memoria.
  }
  return next;
}

/**
 * @param {string|null|undefined} userKey
 * @param {string} [scope]
 */
export function clearNlSearchRecentQueries(userKey, scope = NL_SEARCH_RECENT_SCOPE_MAP) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(nlSearchRecentStorageKey(userKey, scope));
  } catch {
    // ignore
  }
}

/**
 * Logout: borra Recientes de mapa y de lista compartida.
 * @param {string|null|undefined} userKey
 */
export function clearAllNlSearchRecentQueries(userKey) {
  clearNlSearchRecentQueries(userKey, NL_SEARCH_RECENT_SCOPE_MAP);
  clearNlSearchRecentQueries(userKey, NL_SEARCH_RECENT_SCOPE_SHARED);
}

/**
 * Mezcla progresiva: recientes arriba; si faltan cupos, rellena con sugeridas
 * (sin duplicar texto respecto a recientes).
 *
 * @param {{
 *   recentQueries?: string[],
 *   suggestedQueries?: string[],
 *   maxItems?: number,
 * }} [options]
 * @returns {{ recentQueries: string[], suggestedQueries: string[] }}
 */
export function buildNlSearchIdleLists({
  recentQueries = [],
  suggestedQueries = NL_SEARCH_SUGGESTED_QUERIES,
  maxItems = NL_SEARCH_IDLE_MAX_ITEMS,
} = {}) {
  const recent = recentQueries
    .map(normalizeQuery)
    .filter(Boolean)
    .slice(0, maxItems);

  const recentKeys = new Set(recent.map(normalizeKey));
  const remaining = Math.max(0, maxItems - recent.length);
  const suggestions = suggestedQueries
    .map(normalizeQuery)
    .filter((query) => query && !recentKeys.has(normalizeKey(query)))
    .slice(0, remaining);

  return {
    recentQueries: recent,
    suggestedQueries: suggestions,
  };
}
